import type { CandidateStatus } from '../../candidates/types/candidate.types';
import type { JobPositionStatus } from './jobPosition.types';

// Application status enum matching backend
export type ApplicationStatus =
  | 'APPLIED'
  | 'SCREENING'
  | 'INTERVIEWING'
  | 'OFFERED'
  | 'HIRED'
  | 'ON_HOLD'
  | 'REJECTED'
  | 'WITHDRAWN';

// Application status configuration for UI
export const APPLICATION_STATUS_CONFIG: Record<
  ApplicationStatus,
  {
    label: string;
    color: 'default' | 'secondary' | 'info' | 'warning' | 'success' | 'destructive' | 'muted';
  }
> = {
  APPLIED: { label: 'Applied', color: 'default' },
  SCREENING: { label: 'Screening', color: 'secondary' },
  INTERVIEWING: { label: 'Interviewing', color: 'info' },
  OFFERED: { label: 'Offered', color: 'warning' },
  HIRED: { label: 'Hired', color: 'success' },
  ON_HOLD: { label: 'On Hold', color: 'warning' },
  REJECTED: { label: 'Rejected', color: 'destructive' },
  WITHDRAWN: { label: 'Withdrawn', color: 'muted' },
};

// All application statuses for iteration
export const ALL_APPLICATION_STATUSES: ApplicationStatus[] = [
  'APPLIED',
  'SCREENING',
  'INTERVIEWING',
  'OFFERED',
  'HIRED',
  'ON_HOLD',
  'REJECTED',
  'WITHDRAWN',
];

// Candidate summary for embedding in responses
export interface CandidateSummary {
  id: string;
  name: string;
  email: string;
  status: CandidateStatus;
  country?: string | null;
}

// Job position summary for embedding in responses
export interface JobPositionSummary {
  id: string;
  title: string;
  department?: string | null;
  location: string;
  status: JobPositionStatus;
}

// Job position candidate link response
export interface JobPositionCandidate {
  id: string;
  jobPositionId: string;
  candidateId: string;
  applicationStatus: ApplicationStatus;
  appliedAt: string;
  notes?: string | null;
  createdAt: string;
  updatedAt: string;
}

// Job position candidate with full candidate details
export interface JobPositionCandidateWithDetails {
  id: string;
  jobPositionId: string;
  applicationStatus: ApplicationStatus;
  appliedAt: string;
  notes?: string | null;
  candidate: CandidateSummary;
  createdAt: string;
  updatedAt: string;
}

// Candidate's job position with full position details
export interface CandidateJobPositionWithDetails {
  id: string;
  candidateId: string;
  applicationStatus: ApplicationStatus;
  appliedAt: string;
  notes?: string | null;
  jobPosition: JobPositionSummary;
  createdAt: string;
  updatedAt: string;
}

// Request DTOs
export interface AddCandidateToJobPositionRequest {
  // For linking existing candidate
  candidateId?: string;
  // For creating new candidate
  name?: string;
  email?: string;
  country?: string | null;
  // Common
  notes?: string | null;
}

export interface LinkExistingCandidateRequest {
  candidateId: string;
  notes?: string | null;
}

export interface CreateAndLinkCandidateRequest {
  name: string;
  email: string;
  country?: string | null;
  notes?: string | null;
}

export interface UpdateApplicationRequest {
  applicationStatus?: ApplicationStatus;
  notes?: string | null;
}

// Query parameters for listing
export interface ListJobPositionCandidatesParams {
  status?: ApplicationStatus;
}

// Valid status transitions
export const VALID_STATUS_TRANSITIONS: Record<ApplicationStatus, ApplicationStatus[]> = {
  APPLIED: ['SCREENING', 'ON_HOLD', 'REJECTED', 'WITHDRAWN'],
  SCREENING: ['INTERVIEWING', 'ON_HOLD', 'REJECTED', 'WITHDRAWN'],
  INTERVIEWING: ['OFFERED', 'ON_HOLD', 'REJECTED', 'WITHDRAWN'],
  OFFERED: ['HIRED', 'ON_HOLD', 'REJECTED', 'WITHDRAWN'],
  HIRED: [], // Terminal state
  ON_HOLD: ['SCREENING', 'INTERVIEWING', 'OFFERED', 'REJECTED', 'WITHDRAWN'],
  REJECTED: ['APPLIED'], // Reopen
  WITHDRAWN: ['APPLIED'], // Reopen
};

// Helper to check if transition is valid
export function canTransitionTo(from: ApplicationStatus, to: ApplicationStatus): boolean {
  return VALID_STATUS_TRANSITIONS[from].includes(to);
}

// Helper to get available transitions
export function getAvailableTransitions(current: ApplicationStatus): ApplicationStatus[] {
  return VALID_STATUS_TRANSITIONS[current];
}
